import { useState, useMemo } from 'react'
import { useLyricStore } from '../../../../stores/lyricStore'
import type { SavedLyric } from '@creanizer/types'
import { Card } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@creanizer/ui'
import { 
  Eye, Trash2, Music, Calendar, FileText, Search, 
  Filter, SortAsc, SortDesc, Heart, Tag, X, Plus, RotateCcw, Edit 
} from 'lucide-react'
import { LyricResult } from './LyricResult'
import type { LyricInput } from '@creanizer/types'

type SortOrder = 'date-desc' | 'date-asc' | 'title-asc' | 'title-desc' | 'genre'
type FilterOptions = {
  genre: string
  language: string
  mood: string
  onlyFavorites: boolean
  tags: string[]
}

interface SavedLyricsListProps {
  onLoadInput?: (input: LyricInput) => void
  onRegenerate?: (input: LyricInput) => void
}

export function SavedLyricsList({ onLoadInput, onRegenerate }: SavedLyricsListProps) {
  const { lyrics, deleteLyric, toggleFavorite, updateLyric } = useLyricStore()
  const [selectedLyric, setSelectedLyric] = useState<SavedLyric | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOrder, setSortOrder] = useState<SortOrder>('date-desc')
  const [filters, setFilters] = useState<FilterOptions>({
    genre: 'all',
    language: 'all',
    mood: 'all',
    onlyFavorites: false,
    tags: []
  })
  const [showFilters, setShowFilters] = useState(false)
  const [newTag, setNewTag] = useState('')
  const [editingTagId, setEditingTagId] = useState<string | null>(null)

  // 検索・フィルター・ソート処理
  const processedLyrics = useMemo(() => {
    let result = [...lyrics]

    // 検索フィルター
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(lyric => {
        const title = lyric.result.basicInfo.title.toLowerCase()
        const concept = lyric.result.basicInfo.concept?.toLowerCase() || ''
        const content = lyric.result.content.fullLyric.toLowerCase()
        const tags = lyric.tags?.join(' ').toLowerCase() || ''
        return title.includes(query) || 
               concept.includes(query) || 
               content.includes(query) ||
               tags.includes(query)
      })
    }

    // ジャンルフィルター
    if (filters.genre !== 'all') {
      result = result.filter(lyric => lyric.result.basicInfo.genre === filters.genre)
    }

    // 言語フィルター
    if (filters.language !== 'all') {
      result = result.filter(lyric => lyric.result.basicInfo.language === filters.language)
    }

    // ムードフィルター
    if (filters.mood !== 'all') {
      result = result.filter(lyric => lyric.result.basicInfo.mood === filters.mood)
    }

    // お気に入りフィルター
    if (filters.onlyFavorites) {
      result = result.filter(lyric => lyric.isFavorite)
    }

    // タグフィルター
    if (filters.tags.length > 0) {
      result = result.filter(lyric => {
        if (!lyric.tags) return false
        return filters.tags.some(tag => lyric.tags?.includes(tag))
      })
    }

    // ソート処理
    result.sort((a, b) => {
      switch (sortOrder) {
        case 'date-desc':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        case 'date-asc':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        case 'title-asc':
          return a.result.basicInfo.title.localeCompare(b.result.basicInfo.title)
        case 'title-desc':
          return b.result.basicInfo.title.localeCompare(a.result.basicInfo.title)
        case 'genre':
          return a.result.basicInfo.genre.localeCompare(b.result.basicInfo.genre)
        default:
          return 0
      }
    })

    return result
  }, [lyrics, searchQuery, sortOrder, filters])

  // 利用可能なジャンル、言語、ムードを取得
  const availableFilters = useMemo(() => {
    const genres = new Set<string>()
    const languages = new Set<string>()
    const moods = new Set<string>()
    const tags = new Set<string>()

    lyrics.forEach(lyric => {
      genres.add(lyric.result.basicInfo.genre)
      languages.add(lyric.result.basicInfo.language)
      moods.add(lyric.result.basicInfo.mood)
      lyric.tags?.forEach(tag => tags.add(tag))
    })

    return {
      genres: Array.from(genres).sort(),
      languages: Array.from(languages).sort(),
      moods: Array.from(moods).sort(),
      tags: Array.from(tags).sort()
    }
  }, [lyrics])

  const handleDelete = async (id: string) => {
    try {
      await deleteLyric(id)
      setDeleteId(null)
    } catch (error) {
      console.error('削除に失敗しました:', error)
    }
  }

  const handleToggleFavorite = (id: string) => {
    toggleFavorite(id)
  }

  const handleAddTag = (lyricId: string) => {
    if (!newTag.trim()) return
    
    const lyric = lyrics.find(l => l.id === lyricId)
    if (!lyric) return

    const currentTags = lyric.tags || []
    if (!currentTags.includes(newTag)) {
      updateLyric(lyricId, {
        tags: [...currentTags, newTag.trim()]
      })
    }
    setNewTag('')
    setEditingTagId(null)
  }

  const handleRemoveTag = (lyricId: string, tagToRemove: string) => {
    const lyric = lyrics.find(l => l.id === lyricId)
    if (!lyric) return

    const currentTags = lyric.tags || []
    updateLyric(lyricId, {
      tags: currentTags.filter(tag => tag !== tagToRemove)
    })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (lyrics.length === 0) {
    return (
      <Card className="p-12 text-center">
        <div className="flex flex-col items-center space-y-4">
          <Music className="h-16 w-16 text-muted-foreground/50" />
          <div>
            <h3 className="text-lg font-semibold mb-2">
              保存済みの歌詞がありません
            </h3>
            <p className="text-sm text-muted-foreground">
              生成した歌詞は自動的にここに保存されます
            </p>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <>
      {/* 検索・フィルター・ソートコントロール */}
      <div className="space-y-4 mb-4">
        {/* 検索バー */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="タイトル、内容、タグで検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? 'bg-accent' : ''}
          >
            <Filter className="h-4 w-4 mr-2" />
            フィルター
          </Button>
        </div>

        {/* フィルター詳細 */}
        {showFilters && (
          <Card className="p-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="genre-filter">ジャンル</Label>
                <Select value={filters.genre} onValueChange={(value) => setFilters({...filters, genre: value})}>
                  <SelectTrigger id="genre-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">すべて</SelectItem>
                    {availableFilters.genres.map(genre => (
                      <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="language-filter">言語</Label>
                <Select value={filters.language} onValueChange={(value) => setFilters({...filters, language: value})}>
                  <SelectTrigger id="language-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">すべて</SelectItem>
                    {availableFilters.languages.map(language => (
                      <SelectItem key={language} value={language}>{language}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="mood-filter">ムード</Label>
                <Select value={filters.mood} onValueChange={(value) => setFilters({...filters, mood: value})}>
                  <SelectTrigger id="mood-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">すべて</SelectItem>
                    {availableFilters.moods.map(mood => (
                      <SelectItem key={mood} value={mood}>{mood}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="sort-order">並び順</Label>
                <Select value={sortOrder} onValueChange={(value) => setSortOrder(value as SortOrder)}>
                  <SelectTrigger id="sort-order">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date-desc">
                      <span className="flex items-center">
                        <SortDesc className="h-4 w-4 mr-2" />
                        新しい順
                      </span>
                    </SelectItem>
                    <SelectItem value="date-asc">
                      <span className="flex items-center">
                        <SortAsc className="h-4 w-4 mr-2" />
                        古い順
                      </span>
                    </SelectItem>
                    <SelectItem value="title-asc">タイトル（A-Z）</SelectItem>
                    <SelectItem value="title-desc">タイトル（Z-A）</SelectItem>
                    <SelectItem value="genre">ジャンル別</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* お気に入りフィルター */}
            <div className="flex items-center gap-2">
              <Button
                variant={filters.onlyFavorites ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilters({...filters, onlyFavorites: !filters.onlyFavorites})}
              >
                <Heart className={`h-4 w-4 mr-2 ${filters.onlyFavorites ? 'fill-current' : ''}`} />
                お気に入りのみ
              </Button>
            </div>

            {/* タグフィルター */}
            {availableFilters.tags.length > 0 && (
              <div className="space-y-2">
                <Label>タグでフィルター</Label>
                <div className="flex flex-wrap gap-2">
                  {availableFilters.tags.map(tag => (
                    <Badge
                      key={tag}
                      variant={filters.tags.includes(tag) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => {
                        if (filters.tags.includes(tag)) {
                          setFilters({...filters, tags: filters.tags.filter(t => t !== tag)})
                        } else {
                          setFilters({...filters, tags: [...filters.tags, tag]})
                        }
                      }}
                    >
                      <Tag className="h-3 w-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* 結果数表示 */}
        <div className="text-sm text-muted-foreground">
          {processedLyrics.length}件の歌詞が見つかりました
          {searchQuery && ` (検索: "${searchQuery}")`}
        </div>
      </div>

      <ScrollArea className="h-[600px] pr-4">
        <div className="space-y-4">
          {processedLyrics.map((lyric) => (
            <Card key={lyric.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">
                        {lyric.result.basicInfo.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {lyric.result.basicInfo.concept}
                      </p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleToggleFavorite(lyric.id)}
                    >
                      <Heart className={`h-4 w-4 ${lyric.isFavorite ? 'fill-current text-red-500' : ''}`} />
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">
                      <Music className="w-3 h-3 mr-1" />
                      {lyric.result.basicInfo.genre}
                    </Badge>
                    <Badge variant="outline">
                      {lyric.result.basicInfo.mood}
                    </Badge>
                    <Badge variant="outline">
                      {lyric.result.basicInfo.language}
                    </Badge>
                    {lyric.result.structure.estimatedDuration && (
                      <Badge variant="outline">
                        {lyric.result.structure.estimatedDuration}
                      </Badge>
                    )}
                  </div>

                  {/* タグ表示・編集 */}
                  <div className="flex flex-wrap items-center gap-2">
                    {lyric.tags?.map(tag => (
                      <Badge key={tag} variant="secondary">
                        <Tag className="h-3 w-3 mr-1" />
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(lyric.id, tag)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    {editingTagId === lyric.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleAddTag(lyric.id)
                            }
                          }}
                          placeholder="タグを入力"
                          className="h-7 w-24"
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => handleAddTag(lyric.id)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => {
                            setEditingTagId(null)
                            setNewTag('')
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingTagId(lyric.id)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        タグ追加
                      </Button>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(lyric.createdAt)}
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      {lyric.result.structure.totalLines}行
                    </span>
                  </div>

                  {lyric.input.quickPrompt && (
                    <div className="mt-2 p-2 bg-muted/50 rounded text-sm">
                      <span className="font-medium">プロンプト: </span>
                      <span className="text-muted-foreground">
                        {lyric.input.quickPrompt}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 ml-4">
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => setSelectedLyric(lyric)}
                    title="詳細を見る"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  {onLoadInput && (
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => onLoadInput(lyric.input)}
                      title="設定をロード"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                  {onRegenerate && (
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => onRegenerate(lyric.input)}
                      title="再生成"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => setDeleteId(lyric.id)}
                    title="削除"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </ScrollArea>

      {/* 詳細表示ダイアログ */}
      <Dialog open={!!selectedLyric} onOpenChange={() => setSelectedLyric(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>歌詞の詳細</DialogTitle>
          </DialogHeader>
          {selectedLyric && (
            <LyricResult result={selectedLyric.result} />
          )}
        </DialogContent>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>歌詞を削除しますか？</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            この操作は取り消せません。
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              キャンセル
            </Button>
            <Button variant="destructive" onClick={() => deleteId && handleDelete(deleteId)}>
              削除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}